import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme, makeStyles, Chip, Typography } from '@material-ui/core'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { styles } from './cupsFilter.styles'
import { DataType, FormDialogMode, Input } from '@globaleris/ui.library'

import moment from 'moment'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../hooks'
import { fetchContractsByCups } from '../../contracts.service'
import { fetchClient } from '../../../clients/client.service'
import { notifyError } from '../../../../redux'
import { ChangeOwnerForm } from '../contracts.changeOwner'
import { useHistory } from 'react-router-dom'

export const CHANGE_TYPE = {
  CHANGE_OWNER_ONLY: 'changeOwnerOnly',
  ADM_CHANGE: 'admChange',
  ADM_TEC_CHANGE: 'admTecChange'
} as const

export interface CustomFilterProps {
  onApplyFilter: ({ formData, contractData }) => void
  appliedFilter?: any
}

interface initialModal extends Pick<ChangeOwnerForm, 'cups' | 'newStartDate' | 'newEndDate'> {}
interface formDependenciesProps {
  formState: {
    cups: string
    startDate?: Date
    endDate?: Date
    changeType?: string
  }
}

const CupsFilter: React.FC<CustomFilterProps> = ({ onApplyFilter, appliedFilter = {} }) => {
  const [t] = useTranslation(['translation', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const history = useHistory()

  const [cups, setCups] = useState<string | undefined>(appliedFilter.cups)
  const [startDate, setStartDate] = useState<Date | undefined>(appliedFilter.startDate)
  const [endDate, setEndDate] = useState<Date | undefined>(appliedFilter.endDate)
  const [changeType, setChangeType] = useState<string | undefined>(appliedFilter.changeType)

  const [formDependencies, setFormDependencies] = React.useState<formDependenciesProps>({
    formState: {
      cups: '',
      startDate: undefined,
      endDate: undefined,
      changeType: undefined
    }
  })

  React.useEffect(() => {
    handleFormShow()
  }, [])

  React.useEffect(() => {
    setCups(appliedFilter && appliedFilter.cups)
    setStartDate(appliedFilter && appliedFilter.startDate)
    setEndDate(appliedFilter && appliedFilter.endDate)
    setChangeType(appliedFilter && appliedFilter.changeType)
  }, [appliedFilter])

  const invalidFilterError = errorMsg => {
    return (
      <div>
        <Typography variant="subtitle1" color="error">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: '2px' }}>
              <ErrorOutlineIcon />
            </div>
            <div>{t(`contracts:forms.changeOwner.errorText.${errorMsg || 'generic'}`)}</div>
          </div>
        </Typography>
      </div>
    )
  }

  const prevalidateSearch = async ({ cups, startDate, endDate }): Promise<void> => {
    setFormWaiting(true)
    const queryResult = await queryContractByCUPSDates(`${cups}`, startDate, endDate)
    if (queryResult?.success) {
      setFormMode(FormDialogMode.EDIT)
      setFormSubtitle('')
    } else {
      setFormMode(FormDialogMode.VIEW)
      setFormSubtitle(invalidFilterError(queryResult?.errors))
    }

    queryResult &&
      queryResult.success &&
      setFormDependencies(prevState => ({
        ...prevState,
        formState: {
          ...prevState.formState,
          result: {
            ...queryResult.data,
            changeType: prevState.formState.changeType
          },
          contract: queryResult.contractData
        }
      }))

    setFormWaiting(false)

    return
  }

  const handleCupsChange = async (newCups: string) => {
    if (
      newCups &&
      formDependencies.formState.startDate &&
      formDependencies.formState.endDate &&
      moment(formDependencies.formState.startDate).isValid() &&
      moment(formDependencies.formState.endDate).isValid()
    ) {
      setFormWaiting(true)
      await prevalidateSearch({
        cups: newCups,
        startDate: formDependencies.formState.startDate,
        endDate: formDependencies.formState.endDate
      })
      setFormWaiting(false)
    }
  }

  const handleInitDateChange = async (newStartDate: any) => {
    if (!moment(newStartDate).isValid()) {
      return
    }

    const formState = formDependencies.formState
    let endDate = formState.endDate

    if (
      formState.endDate !== undefined &&
      moment(formState.endDate).isBefore(moment(newStartDate), 'days')
    ) {
      endDate = moment(newStartDate).toDate()
    }

    setFormDependencies(prevState => ({
      ...prevState,
      formState: {
        ...prevState.formState,
        startDate: moment(newStartDate).toDate(),
        endDate: endDate
      }
    }))

    if (
      formState.cups &&
      newStartDate &&
      moment(newStartDate).isValid() &&
      endDate &&
      moment(endDate).isValid() &&
      moment(newStartDate).isSameOrBefore(endDate, 'days')
    ) {
      await prevalidateSearch({
        cups: formState.cups,
        startDate: newStartDate,
        endDate: endDate
      })
    }
  }

  const handleEndDateChange = async (newEndDate: any) => {
    let endDate = newEndDate ? moment(newEndDate).toDate() : undefined
    const formState = formDependencies.formState

    if (
      !moment(newEndDate).isValid() ||
      (formDependencies.formState && formState.endDate === null)
    ) {
      return
    }

    if (
      formState &&
      formState.startDate &&
      endDate &&
      moment(endDate).isBefore(formState.startDate, 'days')
    ) {
      endDate = moment(formState.startDate).isValid()
        ? moment(formState.startDate).toDate()
        : undefined
      setFormDependencies(prevState => ({
        ...prevState,
        formState: {
          ...prevState.formState,
          endDate: endDate
        }
      }))
    } else {
      setFormDependencies(prevState => ({
        ...prevState,
        formState: {
          ...prevState.formState,
          endDate: moment(newEndDate).toDate()
        }
      }))
    }

    if (
      formState.cups &&
      newEndDate &&
      moment(newEndDate).isValid() &&
      formState.startDate &&
      moment(formState.startDate).isValid() &&
      moment(formState.startDate).isSameOrBefore(endDate, 'days')
    ) {
      await prevalidateSearch({
        cups: formState.cups,
        startDate: formState.startDate,
        endDate: endDate
      })
    }
  }

  const queryContractByCUPSDates = async (
    cups: string,
    startDate: Date,
    endDate: Date
  ): Promise<{
    success: boolean
    data?: Partial<ChangeOwnerForm>
    contractData?: any
    errors?: string
  } | null> => {
    if (startDate && endDate && cups) {
      setFormWaiting(true)
      const response = await fetchContractsByCups(cups, 1, startDate, endDate)
      if (response.success) {
        setFormWaiting(false)
        if (response.data && response.data.length === 1) {
          const cupsContract = response.data[0]

          if (cupsContract.productsList === undefined || cupsContract.productsList.length === 0) {
            setFormWaiting(false)
            return { success: false, errors: 'productNotFound' }
          }
          const cupsData: Partial<ChangeOwnerForm> = {
            cups: cups,
            activeContractId: cupsContract.id,
            activeProductId: cupsContract.productsList[0].id,
            activeProductCode: cupsContract.productsList[0].code,
            activeQuoteId: cupsContract.quotesList[0]?.id,
            activeContractDesc: cupsContract.contractCode,
            activeStartDate: cupsContract.startDate,
            activeEndDate: cupsContract.endDate,
            newStartDate: moment(startDate).toDate(),
            newEndDate: moment(endDate).toDate(),
            signDate: moment().toDate(),
            saleDate: moment().toDate()
          }

          if (cupsContract.subjectID) {
            const responseSubject = await fetchClient(cupsContract.subjectID)
            if (responseSubject.success && responseSubject.data?.id) {
              cupsData.activeClientId = cupsContract.subjectID
              cupsData.activeClientDesc = responseSubject.data?.description
            }
          } else {
            // subject not found
            notifyError('Error interno al intentar buscar el cliente.')
          }
          return { success: true, data: cupsData, contractData: cupsContract }
        } else {
          if (response.data && response.data.length > 0) {
            return { success: false, errors: 'multipleContracts' }
          } else {
            return { success: false, errors: 'generic' }
          }
        }
      } else {
        setFormWaiting(false)
        notifyError('Error interno al intentar buscar el contrato.')
        return null
      }
    }
    return null
  }

  const searchFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      const dateInputs = [
        {
          name: 'cups',
          label: t('contracts:forms.changeOwner.inputs.cups'),
          type: DataType.TEXT,
          required: true,
          onChange: (value, formState) => {
            handleCupsChange(value)
          }
        },
        {
          name: 'startDate',
          label: t('translation:components.searchFilter.inputs.startDate'),
          type: DataType.DATE,
          required: true,
          onChange: handleInitDateChange
        },
        {
          name: 'endDate',
          label: t('translation:components.searchFilter.inputs.endDate'),
          type: DataType.DATE,
          required: true,
          minDate: formDependencies.formState?.startDate
            ? moment(formDependencies.formState.startDate).toISOString()
            : undefined,
          onChange: handleEndDateChange
        },
        {
          name: 'changeType',
          label: t('contracts:forms.changeOwner.inputs.changeType'),
          required: true,
          type: DataType.SELECT,
          selectOptions: [
            {
              value: CHANGE_TYPE.CHANGE_OWNER_ONLY,
              label: t('contracts:forms.changeOwner.options.changeOwnerOnly')
            },
            {
              value: CHANGE_TYPE.ADM_CHANGE,
              label: t('contracts:forms.changeOwner.options.admChange')
            },
            {
              value: CHANGE_TYPE.ADM_TEC_CHANGE,
              label: t('contracts:forms.changeOwner.options.admTecChange')
            }
          ],
          onChange: value => {
            setFormDependencies(prevState => ({
              ...prevState,
              formState: {
                ...prevState.formState,
                changeType: value
              }
            }))
          }
        }
      ]

      return {
        id: 'CUPS_FILTER',
        sections: [
          {
            inputs: dateInputs
          }
        ]
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies])

  const {
    openForm,
    updateFormDefinition,
    closeForm,
    setFormWaiting,
    formProps,
    CustomFormDialog,
    setFormSubtitle,
    setFormMode
  } = useFormDialog()

  React.useEffect(() => {
    updateFormDefinition(searchFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFormDefinition])

  const handleFormClose = formData => {
    closeForm()
    if (!formData) {
      history.goBack()
      return
    }

    onApplyFilter({
      formData: {
        ...formData.result,
        changeType: formData.changeType // Get changeType from formState directly,
      },
      contractData: formData.contract
    })
  }

  const handleFormShow = () => {
    openForm({
      definition: searchFormDefinition,
      title: t('contracts:forms.changeOwner.initialModal.title'),
      subtitle: t('contracts:forms.changeOwner.initialModal.subtitle'),
      mode: FormDialogMode.VIEW,
      isWaiting: false,
      onClose: formData => handleFormClose(formData),
      okBtnLabel: t('contracts:forms.changeOwner.initialModal.okBtnLabel'),
      cancelBtnLabel: t('translation:components.searchFilter.actions.cancel'),
      closeBtnLabel: t('translation:components.searchFilter.actions.cancel')
    })

    setFormDependencies(prevState => ({
      ...prevState,
      formState: {
        ...appliedFilter,
        cups: cups,
        startDate: startDate,
        endDate: endDate,
        changeType: changeType
      }
    }))
  }

  return <CustomFormDialog {...formProps}></CustomFormDialog>
}

export default React.memo(CupsFilter)
